/**
 * Reading the injected identity clock: the snapshot the fence measures a settle against, the generation helpers
 * a post-settle cache patch guards on, and the one-time report for a clock nobody wired.
 *
 * SPLIT from `./identity.clock` so that seam stays eager-safe. `@pressreader/authentication-core`'s bootstrap
 * imports `setIdentityClock` at module scope — the eager path — and would otherwise carry this whole read half
 * with it for the sake of one setter call: the logger import and the unwired-clock error string included. Every
 * consumer of the read half is lazy (a fenced queryFn, the policy registry), and the appstore package is
 * `sideEffects: false`, so the re-exports the eager side does not reach are tree-shaken.
 */

import { error } from '@pressreader/logger';

import { getIdentityClock } from './identity.clock';

/** Reported as the identity key while no clock is wired — deliberately neither a user key nor the anonymous key. */
const UNWIRED_IDENTITY_KEY = 'unknown';

/** The identity a fenced queryFn is measured against, and (minus the generation) handed to the queryFn it wraps. */
export interface IdentitySnapshot {
    key: string;
    generation: number;
    isAuthenticated: boolean;
}

let unwiredReported = false;

/**
 * Read the current token identity.
 *
 * Fails OPEN when the clock was never wired (a boot-order regression): a constant generation means the
 * fence never rejects and `isAuthenticated` never blocks a fetch, so behavior degrades to what it was
 * before the fence existed (commit-time reconcile only, server-side auth unchanged) rather than to an
 * app that silently shows empty data. Logged once as an error so the misconfiguration is loud.
 */
export function readIdentity(): IdentitySnapshot {
    const current = getIdentityClock();
    if (!current) {
        reportUnwiredClock();
        return { key: UNWIRED_IDENTITY_KEY, generation: 0, isAuthenticated: true };
    }
    const key = current.key();
    return { key, generation: current.generation(), isAuthenticated: key !== current.anonymousKey };
}

/**
 * Whether a clock was ever wired (`setIdentityClock`).
 *
 * Ask this wherever a caller must tell a REAL identity reading from the fail-open placeholder `readIdentity`
 * returns — notably the commit reconcile's generation dedupe, which must not dedupe against a generation that
 * is a constant. Deliberately silent: unlike `readIdentity` it does not report the misconfiguration, so a
 * caller can branch on it without adding to the single logged error.
 */
export function isIdentityClockWired(): boolean {
    return getIdentityClock() !== undefined;
}

/**
 * The generation to hold onto before starting work whose RESULT will be written into the cache outside a
 * fenced queryFn — pair with `isCurrentGeneration` at write time. The `identityScoped` fence covers only the
 * queryFn itself; an `onQueryStarted` patch after `queryFulfilled` settles later and needs its own guard.
 */
export function currentIdentityGeneration(): number {
    return readIdentity().generation;
}

/**
 * True while the identity generation still equals `generation` (captured via `currentIdentityGeneration`).
 * Guard for post-settle cache patches: a mutation that fulfills after an identity change must not patch the
 * previous identity's row into the new identity's cache — skip the patch and let its `invalidatesTags`
 * refetch truth instead.
 */
export function isCurrentGeneration(generation: number): boolean {
    return readIdentity().generation === generation;
}

function reportUnwiredClock(): void {
    if (unwiredReported) {
        return;
    }
    // Once per session: this is read on every fenced query, and a log storm would bury the message.
    unwiredReported = true;
    error(
        new Error(
            'appstore: no identity clock wired (setIdentityClock was never called) — RTK Query identity fencing is disabled and every query runs as if the identity never changed.',
        ),
    );
}
